import axios from 'axios';
import { message } from 'antd';
import { useUserStore } from '@/stores/userStore';

export type BirthdayCalendar = 'solar' | 'lunar';
export type CrewCertificate = 'fund' | 'securities' | 'cfa' | 'cpa' | 'frm' | 'other';

export interface EmergencyContact {
  name: string;
  phone: string;
}

export interface ProductManagementRecord {
  year: number;
  scale: string;
  returnRate: number | null;
}

export interface CrewProfile {
  userId: string;
  nationalId: string;
  birthday: string;
  birthdayCalendar: BirthdayCalendar;
  hireDate: string | null;
  phone: string;
  homeAddress: string;
  emergencyContact: EmergencyContact;
  lastMedicalCheckDate: string | null;
  certificates: CrewCertificate[];
  certificateOther: string | null;
  productManagementRecords: ProductManagementRecord[];
  createdAt: string;
  updatedAt: string;
}

export interface CrewProfileCompletion {
  percent: number;
  missingRequiredFields: string[];
  filledFields: number;
  totalFields: number;
}

export interface CrewProfileResponse {
  profile: CrewProfile | null;
  completion: CrewProfileCompletion;
}

export interface CrewProfileAdminItem {
  id: string;
  adenId: string;
  username: string;
  email: string;
  phone?: string;
  realName?: string;
  userType?: string;
  profile: CrewProfile | null;
  completion: CrewProfileCompletion;
  updatedAt: string | null;
}

export interface UpsertCrewProfilePayload {
  nationalId: string;
  birthday: string;
  birthdayCalendar: BirthdayCalendar;
  hireDate?: string | null;
  phone: string;
  homeAddress: string;
  emergencyContact: EmergencyContact;
  lastMedicalCheckDate?: string | null;
  certificates: CrewCertificate[];
  certificateOther?: string | null;
  productManagementRecords: ProductManagementRecord[];
}

export const certificateLabels: Record<CrewCertificate, string> = {
  fund: '基金从业资格证',
  securities: '证券从业资格证',
  cfa: 'CFA',
  cpa: 'CPA',
  frm: 'FRM',
  other: '其他',
};

const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 12000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const userInfo = useUserStore.getState().userInfo;
  if (userInfo?.id) {
    config.headers = config.headers || {};
    (config.headers as any)['X-User-Identifier'] = userInfo.id;
  }
  return config;
});

apiClient.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 403) message.error(data?.message || '权限不足');
    else if (status >= 500) message.error('机组信息服务异常，请稍后再试');
    return Promise.reject(error);
  },
);

export const crewProfileGetMine = (): Promise<CrewProfileResponse> =>
  apiClient.get<CrewProfileResponse>('/crew-profiles/me').then((r) => r.data);

export const crewProfileSaveMine = (
  payload: UpsertCrewProfilePayload,
): Promise<CrewProfileResponse> =>
  apiClient.put<CrewProfileResponse>('/crew-profiles/me', payload).then((r) => r.data);

export const crewProfileListAdmin = (): Promise<CrewProfileAdminItem[]> =>
  apiClient.get<CrewProfileAdminItem[]>('/crew-profiles/admin').then((r) => r.data);
